import React from "react";
import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_boarding-school-fit/artifacts/5kmsljm9_ESA%20Final%20Logo.png";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#1A202C] text-[#FDFCF8] py-12 md:py-16">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-10 md:mb-12">
          {/* Logo & Description */}
          <div className="sm:col-span-2 lg:col-span-2">
            <img
              src={LOGO_URL}
              alt="Elite School Advisory"
              className="h-12 md:h-16 w-auto mb-4 md:mb-6 brightness-0 invert"
              data-testid="footer-logo"
            />
            <p className="text-sm md:text-base text-white/60 leading-relaxed max-w-md mb-4 md:mb-6">
              Elite School Advisory provides strategic, personalised guidance for
              families seeking admission to the world's leading boarding schools.
            </p>
            <div className="space-y-2 sm:space-y-3">
              <a href="mailto:eliteschooladvisory@gmail.com" className="flex items-center gap-3 text-sm md:text-base text-white/60 hover:text-[#C9A227] transition-colors py-1">
                <Mail className="w-4 h-4 text-[#C9A227] flex-shrink-0" />
                <span>eliteschooladvisory@gmail.com</span>
              </a>
              <a href="tel:+919818065496" className="flex items-center gap-3 text-sm md:text-base text-white/60 hover:text-[#C9A227] transition-colors py-1">
                <Phone className="w-4 h-4 text-[#C9A227] flex-shrink-0" />
                <span>+91 98180 65496</span>
              </a>
              <div className="flex items-center gap-3 text-sm md:text-base text-white/60 py-1">
                <MapPin className="w-4 h-4 text-[#C9A227] flex-shrink-0" />
                <span>New Delhi, India</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-3 sm:gap-4 mt-4 md:mt-6">
              <a
                href="#instagram"
                className="w-11 h-11 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C9A227] transition-colors duration-300"
                data-testid="social-instagram"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5 text-white" />
              </a>
              <a
                href="#facebook"
                className="w-11 h-11 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C9A227] transition-colors duration-300"
                data-testid="social-facebook"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5 text-white" />
              </a>
              <a
                href="#linkedin"
                className="w-11 h-11 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C9A227] transition-colors duration-300"
                data-testid="social-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5 text-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4
              className="text-base md:text-lg font-medium mb-4 md:mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Quick Links
            </h4>
            <ul className="space-y-2 sm:space-y-3">
              {[
                { label: "Our Approach", href: "#method" },
                { label: "Services", href: "#services" },
                { label: "Schools", href: "#schools" },
                { label: "FAQs", href: "#faq" },
                { label: "Contact", href: "#contact" },
              ].map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm md:text-base text-white/60 hover:text-[#C9A227] transition-colors py-1 inline-block"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4
              className="text-base md:text-lg font-medium mb-4 md:mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Legal
            </h4>
            <ul className="space-y-2 sm:space-y-3">
              {[
                { label: "Privacy Policy", href: "/privacy" },
                { label: "Terms of Service", href: "/terms" },
                { label: "Cookie Policy", href: "/cookies" },
                { label: "Refund Policy", href: "/refund" },
              ].map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm md:text-base text-white/60 hover:text-[#C9A227] transition-colors py-1 inline-block"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 md:pt-8 border-t border-white/10">
          <div className="text-center mb-4 md:mb-6">
            <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-3xl mx-auto">
              <span className="text-[#C9A227] font-medium">Elite School Advisory</span> is the boarding school program by <span className="text-white font-medium">Mindways Consulting Private Limited</span> — an established name in international education since 2005.
            </p>
          </div>
          <div className="pt-4 md:pt-6 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4">
            <p className="text-white/40 text-xs sm:text-sm text-center md:text-left">
              &copy; {currentYear} Elite School Advisory. All rights reserved.
            </p>
            <p className="text-white/40 text-xs sm:text-sm text-center md:text-right">
              New Delhi, India
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
